import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccessibility } from '../../context/AccessibilityContext/AccessibilityContext'
import style from './link.module.scss'

const Link = ({
  children, 
  href, 
  color, 
  fontSize, 
  fontFamily, 
  fontWeight, 
  dropDownItems, 
  textDecoration, 
  underline = true,
  variant = 'default' // 'default' or 'gradient'
}) => {
    const [isHovered, setIsHovered] = useState(false)
    const [isClicked, setIsClicked] = useState(false)
    const timeoutRef = useRef(null)
    const clickTimeoutRef = useRef(null)
    const navigate = useNavigate()
    const { settings } = useAccessibility()

    const config = {
        color: variant === 'gradient' 
            ? '#FFFFFF' // White text for gradient background
            : settings.contrast !== 'default' 
                ? 'var(--text-primary)' 
                : color || 'var(--text-primary)',
        fontSize: `calc(${fontSize || '0.875rem'} * var(--font-scale, 1))`,
        fontWeight: fontWeight || '500',
        fontFamily: fontFamily || 'Montserrat, sans-serif',
        background: variant === 'gradient' 
            ? 'linear-gradient(90deg, #E22B36 0%, #1d3e8c 100%)'
            : 'transparent',
        transition: 'all 0.3s ease',
        textDecoration: textDecoration || 'none',
        padding: variant === 'gradient' ? '0.75rem 1.5rem' : '0.5rem 0.25rem',
        borderRadius: variant === 'gradient' ? '8px' : '0'
    }

    const handleMouseEnter = () => {
        clearTimeout(timeoutRef.current)
        setIsHovered(true)
    }

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsHovered(false)
        }, 300)
    }

    const handleMouseDown = () => {
        setIsClicked(true)
    }

    const handleMouseUp = () => {
        setIsClicked(false)
    }

    const handleClick = (e) => {
        // Click animation
        setIsClicked(true)
        clearTimeout(clickTimeoutRef.current)
        clickTimeoutRef.current = setTimeout(() => {
            setIsClicked(false)
        }, 300)

        if (dropDownItems) {
            e.preventDefault()
        } else if (href && href.startsWith('#')) {
            e.preventDefault()
            const targetId = href.substring(1)
            const targetElement = document.getElementById(targetId)
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth' })
            }
        } else if (href && (href.startsWith('http') || href.startsWith('https'))) {
            e.preventDefault()
            window.open(href, '_blank')
        } else if (href) {
            e.preventDefault()
            window.scrollTo({ top: 0, behavior: 'smooth' })
            navigate(href)
        }
    }

    // Clean up timeouts on unmount
    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current)
            clearTimeout(clickTimeoutRef.current)
        }
    }, [])

    return (
        <div
            className={`${style.container} ${variant === 'gradient' ? style.gradient : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onFocus={handleMouseEnter}
            onBlur={handleMouseLeave}
            data-hovered={isHovered}
            data-clicked={isClicked}
            data-variant={variant}
        >
            <a
                className={style.link}
                style={config}
                onClick={handleClick}
                href={href || '#'}
                aria-haspopup={!!dropDownItems}
                aria-expanded={isHovered}
            >
                {children}
                {underline && variant === 'default' && <span className={style.underline} />}
                <span className={style.ripple} />
            </a>

            {dropDownItems && isHovered && (
                <div 
                    className={style.dropdown}
                    role="menu"
                >
                    {dropDownItems}
                </div>
            )}
        </div>
    )
}

export default Link